import { css } from '@emotion/react'
import { t, Trans } from '@lingui/macro'
import { Formik } from 'formik'
import { FullBlockSpinner } from 'front-end/src/components'
import {
  EventRecurrencePattern,
  RecurringEventAttendanceTypeEnum
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { ManageEventNotification } from 'front-end/src/pages/manage-events/event-edit/ManageEventNotification'
import React from 'react'
import { Link, Route, Switch, useParams, useRouteMatch } from 'react-router-dom'
import { brandColors } from 'shared/basicPalette'
import { Dayjs } from 'shared/dayjs'
import { linkBuilder, staticLinks } from 'shared/linkBuilder'
import { navigateTo } from '../../../app/util/appHistory'
import Message, {
  MessageTypes
} from '../../../components/common/message/Message'
import { notification } from '../../../components/common/notifications/Notifications'
import { Page, PageContentWrapper } from '../../../components/common/page/Page'
import { ManageEventContext } from '../ManageEventContext'
import {
  EventDetailEditFragment,
  EventDetailsForEditDocument,
  useEventDetailsForEditQuery,
  useEventPatchMutation,
  usePatchEventAndNotifyMutation,
  usePublishEventMutation
} from './EventEdit.codegen'
import './EventEdit.less'
import {
  deserializeEventTimesForManage,
  prepareEventPayload,
  prepareRecurringEventPayload
} from './helpers'
import { ManageEventTopbar } from './ManageEventTopbar'
import {
  RecurringEventDetailEditDocument,
  RecurringEventDetailFragment,
  useCreateRecurringEventBasedOnRegularEventMutation,
  usePatchRecurringEventAndNotifyMutation,
  usePublishRecurringEventMutation,
  useRecurringEventDetailEditQuery,
  useRecurringEventPatchMutation,
  useTransformRecurringEventToEventMutation
} from './RecurringEventEdit.codegen'
import AttendanceTab from './tabs/attendance/AttendanceTab'
import EventDetail from './tabs/event-detail/EventDetail'
import { OccurrencesTab } from './tabs/occurences/OccurrencesTab'

export type ManageEventDetailDayjs = (
  | EventDetailEditFragment
  | RecurringEventDetailFragment
) & {
  start_time: Dayjs
  end_time: Dayjs
  recurring_event_id?: number
  attendance_type?: RecurringEventDetailFragment['attendance_type']
  relatedCertifications?: EventDetailEditFragment['relatedCertifications']
  recurringEvent?: EventDetailEditFragment['recurringEvent']
  recurrence?: EventRecurrencePattern
  google_meet_id?: string
  ms_teams_enabled?: boolean
  ms_teams_url?: string
}

export const useEventForEditingQuery = (eventId: number) => {
  const { url } = useRouteMatch()
  const isRecurring = url.includes('series')

  const queryOptions = {
    variables: {
      eventId
    },
    notifyOnNetworkStatusChange: true
  }

  if (isRecurring) {
    return useRecurringEventDetailEditQuery(queryOptions)
  }
  return useEventDetailsForEditQuery(queryOptions)
}

export const EventEditPage = () => {
  const { url } = useRouteMatch()
  const isRecurringSeries = url.includes('series')
  const { eventId } = useParams<{ eventId: string }>()
  const [createRecurringEventBasedOnRegularEvent] =
    useCreateRecurringEventBasedOnRegularEventMutation({
      onCompleted: (data) => {
        if (data?.admin?.createRecurringEventBasedOnRegularEvent?.id) {
          navigateTo(
            linkBuilder.manageEvent(
              data?.admin?.createRecurringEventBasedOnRegularEvent?.id,
              true
            ),
            false,
            true
          )
        }
      }
    })
  const [transformRecurringEventToEvent] =
    useTransformRecurringEventToEventMutation({
      onCompleted: (data) => {
        const newEventId =
          data?.admin.recurringEvent.transformRecurringEventToRegularEvent.id

        refetch()
        notification.info(t`Event series was transferred to single event.`)
        if (newEventId) {
          navigateTo(linkBuilder.manageEvent(newEventId))
        }
      },
      onError: () =>
        notification.error(t`Saving event series has failed. Try again later.`)
    })

  const { data, loading, refetch } = useEventForEditingQuery(Number(eventId))
  const [recurringEventPatchMutation] = useRecurringEventPatchMutation({
    onCompleted: () => {
      refetch()
    },
    onError: () =>
      notification.error(t`Saving event series has failed. Try again later.`)
  })
  const [patchRecurringEventAndNotifyMutation] =
    usePatchRecurringEventAndNotifyMutation({
      onCompleted: () => {
        refetch()
      },
      onError: () =>
        notification.error(t`Saving event series has failed. Try again later.`)
    })
  const [patchEventAndNotifyMutation] = usePatchEventAndNotifyMutation({
    onCompleted: () => {
      refetch()
    },
    onError: () =>
      notification.error(t`Saving event has failed. Try again later.`)
  })
  const [eventPatchMutation] = useEventPatchMutation({
    onCompleted: () => {
      refetch()
    },
    onError: () =>
      notification.error(t`Saving event has failed. Try again later.`)
  })
  const [publishEventMutation] = usePublishEventMutation({
    refetchQueries: [EventDetailsForEditDocument, 'useEventForEditingQuery']
  })
  const [publishRecurringEventMutation] = usePublishRecurringEventMutation({
    refetchQueries: [
      RecurringEventDetailEditDocument,
      'useEventForEditingQuery'
    ]
  })

  const updateEvent = async ({
    payload,
    notifyInvitees,
    notifyRSVPdInvitees,
    emailIdentityId,
    publish
  }: {
    payload: Partial<ManageEventDetailDayjs>
    notifyInvitees?: boolean
    notifyRSVPdInvitees?: boolean
    emailIdentityId?: number | null
    publish?: boolean
  }) => {
    let createdSeriesId: number | null = null
    if (!event?.id) {
      return
    }
    const preparedPayload = prepareEventPayload(payload)
    const variables = {
      data: preparedPayload,
      eventId: event.id,
      tags: payload.tags?.map((value) => value.id) || []
    }

    const notificationText = publish
      ? t`Event is publishing. Please do not leave the page.`
      : t`Event was saved successfully`

    let patchError = null

    const patchMutation =
      notifyInvitees || notifyRSVPdInvitees
        ? patchEventAndNotifyMutation({
            variables: {
              ...variables,
              emailIdentityId,
              notifyInvitees,
              notifyRSVPdInvitees
            }
          })
        : eventPatchMutation({
            variables
          })

    await patchMutation.then((result) => {
      const err = result?.errors?.message
      if (err) {
        patchError = err
      } else {
        notification.info(notificationText)
      }
    })

    if (patchError) {
      return
    }

    if (!event.recurring_event_id && payload.recurringEvent?.recurrence?.freq) {
      const { data } = await createRecurringEventBasedOnRegularEvent({
        variables: {
          eventId: event.id,
          recurrence: {
            freq: payload.recurringEvent?.recurrence?.freq,
            byDay: payload.recurringEvent?.recurrence?.byDay,
            count: payload.recurringEvent?.recurrence?.count,
            interval: payload.recurringEvent?.recurrence?.interval
          },
          attendanceType:
            payload.attendance_type || RecurringEventAttendanceTypeEnum.all
        }
      })

      createdSeriesId = data?.admin?.createRecurringEventBasedOnRegularEvent?.id
    }

    if (!event.published_date && publish) {
      if (createdSeriesId) {
        await publishRecurringEventMutation({
          variables: { eventId: createdSeriesId, timeZone: event.timezone }
        })
      } else {
        await publishEventMutation({
          variables: { id: event.id }
        })
      }
    }
  }

  const updateRecurringEventSeries = async ({
    payload,
    notifyInvitees,
    notifyRSVPdInvitees,
    emailIdentityId,
    publish
  }: {
    payload: Partial<ManageEventDetailDayjs>
    notifyInvitees?: boolean
    notifyRSVPdInvitees?: boolean
    emailIdentityId?: number | null
    publish?: boolean
  }) => {
    if (!event?.id) {
      return
    }
    const notificationTextRecurrence = publish
      ? t`Event series was published successfully`
      : t`Event series was saved successfully`

    if (!payload.recurrence?.freq) {
      await transformRecurringEventToEvent({
        variables: {
          recEventId: event.id
        }
      })
    } else {
      const variables = {
        data: prepareRecurringEventPayload(payload),
        eventId: event.id,
        tags: payload.tags?.map((value) => value.id) || []
      }

      let patchError = null

      const patchRecurringEventMutation =
        notifyInvitees || notifyRSVPdInvitees
          ? patchRecurringEventAndNotifyMutation({
              variables: {
                ...variables,
                notifyInvitees,
                notifyRSVPdInvitees,
                emailIdentityId
              }
            })
          : recurringEventPatchMutation({
              variables
            })

      await patchRecurringEventMutation.then((result) => {
        const err = result?.errors?.message
        if (err) {
          patchError = err
        } else {
          notification.info(notificationTextRecurrence)
        }
      })

      if (patchError) {
        return
      }

      if (!event.published_date && publish) {
        await publishRecurringEventMutation({
          variables: { eventId: event.id, timeZone: event.timezone }
        })
      }
    }
  }

  if (!data && loading) {
    return <FullBlockSpinner />
  }

  const handleSave = async (newEvent: ManageEventDetailDayjs) => {
    if (isRecurringSeries) {
      await updateRecurringEventSeries({
        payload: newEvent as Partial<ManageEventDetailDayjs>
      })
    } else {
      await updateEvent({
        payload: newEvent as Partial<ManageEventDetailDayjs>
      })
    }
  }

  const event = data?.admin?.event || data?.admin?.recurringEvent

  if (!event) {
    return (
      <div className="page--error">
        <Message type={MessageTypes.WARNING}>
          <div>
            <Trans>Unable to load an event.</Trans>
          </div>
          <div>
            <Trans>
              Please click <Link to={staticLinks.manageEvents}>here</Link> to
              return to dashboard
            </Trans>
          </div>
        </Message>
      </div>
    )
  }

  const deserializedEvent = deserializeEventTimesForManage(
    event
  ) as ManageEventDetailDayjs

  return (
    <ManageEventContext.Provider
      value={{
        event: deserializedEvent,
        updateEvent,
        updateRecurringEventSeries,
        isRecurringSeries: isRecurringSeries,
        isRecurringInstance: !!event.recurring_event_id
      }}
    >
      <Formik
        initialValues={deserializedEvent}
        onSubmit={handleSave}
        enableReinitialize
        validateOnChange={false}
      >
        <>
          <ManageEventTopbar event={event} />
          <Page
            withTopbar
            bgColor={brandColors.white}
            css={css`
              .Page__inner {
                max-width: 996px;
              }
            `}
          >
            <PageContentWrapper maxW="964px" mt="56px">
              <ManageEventNotification />
              <Switch>
                <Route
                  exact
                  path={linkBuilder.manageEvent(event.id, isRecurringSeries)}
                  render={() => <EventDetail />}
                />
                <Route
                  path={linkBuilder.manageEvent(
                    event.id,
                    isRecurringSeries,
                    'attendance'
                  )}
                  render={() => <AttendanceTab />}
                />
                <Route
                  path={linkBuilder.manageEvent(
                    event.id,
                    isRecurringSeries,
                    'occurrences'
                  )}
                  render={() => <OccurrencesTab />}
                />
              </Switch>
            </PageContentWrapper>
          </Page>
        </>
      </Formik>
    </ManageEventContext.Provider>
  )
}
